/**
 * 远程屏幕展示组件：绑定鼠标键盘事件并按"远程输入协议 v2"上送。
 *
 * 协议契约：docs/reference/protocol/remote-input.md
 *
 * 通道分发：
 *   - control 通道：键盘、文本、鼠标按键、滚轮、握手、模式、释放、剪贴板
 *   - cursor 通道：mouseMove / mouseMoveRelative
 *
 * 副作用清单（按协议 §7.4 / §8）：
 *   - data channel open 后立刻发送 hello
 *   - blur / visibilitychange → hidden / 主动断开前发送 releaseAll
 */

// biome-ignore-all lint/a11y/noNoninteractiveTabindex: 远控容器必须进入 Tab 顺序以捕获键盘和 IME 输入。
import type { FocusEvent, RefObject, VideoHTMLAttributes } from "react";
import { useEffect, useRef } from "react";
import winPaper from "@/components/remote/device-overview/windows_wallpaper.jpg";
import {
	compositionEndHandler,
	getCurrentPeer,
	keyDownEventHandler,
	keyUpEventHandler,
	mouseButtonEventHandler,
	mouseWheelEventHandler,
	preventContextMenuHandler,
	sendReleaseAll,
	textInputHandler,
	throttledMouseMoveEventHandler,
	updateDeviceResolution,
} from "@/services/remote/webrtc";
import { useWebRTCStore } from "@/stores/remote";

type RemoteScreenProps = {
	videoRef: RefObject<HTMLVideoElement>;
} & VideoHTMLAttributes<HTMLVideoElement>;

/**
 * 远程屏幕展示组件，接收 videoRef 以便父级直接操作视频标签。
 * @param {RemoteScreenProps} props 组件参数
 */
// eslint-disable-next-line react/display-name
export const RemoteScreen = ({
	videoRef,
	className,
	...restProps
}: RemoteScreenProps) => {
	const stream = useWebRTCStore((state) => state.stream);
	const iceConnectionState = useWebRTCStore(
		(state) => state.iceConnectionState,
	);

	/**
	 * 用于聚焦键盘事件的容器（替代 document，避免与全局快捷键冲突）。
	 */
	const captureRef = useRef<HTMLDivElement | null>(null);

	/**
	 * 隐藏输入框，承接 input/composition 事件以支持普通打字与 IME。
	 */
	const imeRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (!videoRef?.current) return;

		const video_element = videoRef.current;
		const captureEl = captureRef.current;
		const imeEl = imeRef.current;

		const peer = getCurrentPeer();
		// 通道引用要每次现取——`reinitializeWithNewIceConfig` 会把 dc_control /
		// dc_cursor 整套换成新对象，effect 只在 mount 时跑一次，
		// 闭包里的旧引用是 closed 状态，输入会静默失效。
		// peer 是 WebRTCConnector 单例本身，引用稳定；其 getter 总返回最新通道。
		const getControl = () => peer.getDataChannel();
		const getCursor = () => peer.getCursorChannel();
		// hello 的发送已下沉到 WebRTCConnector 的 control onOpen 钩子里，
		// reinit 后会自动重发；组件这一层不再负责。

		updateDeviceResolution();

		// ---- 鼠标事件（pointer events，便于 setPointerCapture 兜底） ----
		//
		// 用 pointer events 替代 mouse events 的关键收益：pointerdown 后调用
		// setPointerCapture，即便用户拖出 video 区域释放，pointerup 仍会回到 video
		// 元素上，避免远端按钮被"卡按"。
		// PointerEvent 继承自 MouseEvent，可直接复用 mouseButton / mouseMove handler。
		const onPointerMove = (event: Event) => {
			throttledMouseMoveEventHandler(getCursor(), event as PointerEvent);
		};
		const onPointerDown = (event: Event) => {
			const pe = event as PointerEvent;
			// 仅捕获主指针，避免触屏多指捕获导致后续事件路由错乱
			if (
				pe.isPrimary &&
				typeof video_element.setPointerCapture === "function"
			) {
				try {
					video_element.setPointerCapture(pe.pointerId);
				} catch (error) {
					console.warn("[remote-screen] setPointerCapture failed:", error);
				}
			}
			mouseButtonEventHandler(getControl(), pe);
		};
		const releasePointerCapture = (pe: PointerEvent) => {
			if (typeof video_element.releasePointerCapture === "function") {
				try {
					video_element.releasePointerCapture(pe.pointerId);
				} catch {
					// 没捕获过会抛 InvalidStateError，忽略即可
				}
			}
		};
		const onPointerUp = (event: Event) => {
			const pe = event as PointerEvent;
			mouseButtonEventHandler(getControl(), pe);
			releasePointerCapture(pe);
		};
		// pointercancel 常不带有效 button；用 releaseAll 作为系统打断的兜底释放。
		const onPointerCancel = (event: Event) => {
			const pe = event as PointerEvent;
			sendReleaseAll(getControl());
			releasePointerCapture(pe);
		};
		const onContextMenu = (event: Event) => {
			preventContextMenuHandler(event as MouseEvent);
		};
		const onWheel = (event: Event) => {
			mouseWheelEventHandler(getControl(), event as WheelEvent);
		};

		const elementEvents: Array<
			[string, (event: Event) => void, AddEventListenerOptions?]
		> = [
			["pointermove", onPointerMove],
			["pointerdown", onPointerDown],
			["pointerup", onPointerUp],
			["pointercancel", onPointerCancel],
			["contextmenu", onContextMenu],
			// wheel 默认 passive，需要显式 passive:false 才能 preventDefault
			["wheel", onWheel, { passive: false }],
		];
		elementEvents.forEach(([event, handler, options]) => {
			video_element.addEventListener(event, handler, options);
		});

		// ---- 键盘事件（绑定到捕获容器，避免污染全局） ----

		/**
		 * 当前修饰键按下状态，用于 textInput 抑制判定。
		 * 协议 §8：!meta && (altGraph || (!ctrl && !alt)) 时才发 textInput。
		 */
		const modifiersDown = {
			ctrl: false,
			alt: false,
			meta: false,
			altGraph: false,
		};

		/**
		 * 最近一次 keydown 的 `event.code`，用于 Numpad 双轨抑制（协议 §9.5）。
		 */
		let lastKeyDownCode: string | null = null;

		const updateModifierStateFromEvent = (event: KeyboardEvent) => {
			// AltGraph 状态来自 getModifierState，瞬时跟随事件
			modifiersDown.altGraph =
				typeof event.getModifierState === "function"
					? event.getModifierState("AltGraph")
					: false;
		};

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Control") modifiersDown.ctrl = true;
			else if (event.key === "Alt") modifiersDown.alt = true;
			else if (event.key === "Meta") modifiersDown.meta = true;
			updateModifierStateFromEvent(event);
			lastKeyDownCode = event.code;
			keyDownEventHandler(getControl(), event);
		};
		const onKeyUp = (event: KeyboardEvent) => {
			if (event.key === "Control") modifiersDown.ctrl = false;
			else if (event.key === "Alt") modifiersDown.alt = false;
			else if (event.key === "Meta") modifiersDown.meta = false;
			updateModifierStateFromEvent(event);
			keyUpEventHandler(getControl(), event);
		};

		/** 普通打字：input 事件携带 inputType + data，过滤掉 composition 阶段 */
		let composing = false;
		const onInput = (event: Event) => {
			if (composing) return;
			const ie = event as InputEvent;
			if (ie.inputType !== "insertText") return;
			textInputHandler(
				getControl(),
				ie.data ?? "",
				modifiersDown,
				lastKeyDownCode,
			);
			// 立即清空 IME 输入框，避免持续累积内容
			if (imeEl) imeEl.value = "";
		};
		const onCompositionStart = () => {
			composing = true;
		};
		const onCompositionEnd = (event: CompositionEvent) => {
			composing = false;
			compositionEndHandler(getControl(), event);
			if (imeEl) imeEl.value = "";
		};

		const captureTarget: HTMLElement | Document = captureEl ?? document;
		captureTarget.addEventListener("keydown", onKeyDown as EventListener);
		captureTarget.addEventListener("keyup", onKeyUp as EventListener);

		// IME / 文本输入挂在隐藏 contentEditable 上
		if (imeEl) {
			imeEl.addEventListener("input", onInput);
			imeEl.addEventListener("compositionstart", onCompositionStart);
			imeEl.addEventListener(
				"compositionend",
				onCompositionEnd as EventListener,
			);
		}

		// ---- 全局生命周期：blur / visibilitychange → releaseAll ----
		const onBlur = () => {
			sendReleaseAll(getControl());
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				sendReleaseAll(getControl());
			}
		};
		window.addEventListener("blur", onBlur);
		document.addEventListener("visibilitychange", onVisibilityChange);

		// 容器自动获取焦点，方便用户进入后即可输入
		if (captureEl) {
			// 异步聚焦：避开 React 渲染时序
			requestAnimationFrame(() => {
				imeEl?.focus({ preventScroll: true });
			});
		}

		return () => {
			// 卸载前发送 releaseAll，避免远端"卡键"
			sendReleaseAll(getControl());

			elementEvents.forEach(([event, handler, options]) => {
				video_element?.removeEventListener(event, handler, options);
			});
			captureTarget.removeEventListener("keydown", onKeyDown as EventListener);
			captureTarget.removeEventListener("keyup", onKeyUp as EventListener);
			if (imeEl) {
				imeEl.removeEventListener("input", onInput);
				imeEl.removeEventListener("compositionstart", onCompositionStart);
				imeEl.removeEventListener(
					"compositionend",
					onCompositionEnd as EventListener,
				);
			}
			window.removeEventListener("blur", onBlur);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, [videoRef]);

	useEffect(() => {
		if (iceConnectionState !== "connected") return;
		console.log("[remote Screen] stream is:", stream);
		if (!videoRef?.current) return;
		videoRef.current.autoplay = true;
		videoRef.current.srcObject = stream as MediaStream;

		// 流断开时浏览器不会自动清理，需要手动解除绑定
		return () => {
			if (videoRef.current) {
				videoRef.current.srcObject = null;
			}
		};
	}, [iceConnectionState, stream, videoRef]);

	if (iceConnectionState !== "connected") return null;

	/**
	 * 把输入焦点放到隐藏输入框，确保普通文本与 IME 事件进入远控协议。
	 */
	const focusImeInput = () => {
		imeRef.current?.focus({ preventScroll: true });
	};

	/**
	 * 容器通过 Tab 获得焦点时，把焦点转给隐藏输入框。
	 * 子输入框的 focus 会冒泡，因此仅处理容器自身成为 target 的情况。
	 */
	const handleContainerFocus = (event: FocusEvent<HTMLDivElement>) => {
		if (event.target === event.currentTarget) {
			focusImeInput();
		}
	};

	return (
		<div
			ref={captureRef}
			role="application"
			tabIndex={0}
			onFocus={handleContainerFocus}
			onMouseDown={focusImeInput}
			aria-label="Remote control screen. Keyboard input is sent to the remote device."
			className="relative flex h-full w-full items-center justify-center bg-gray-500 px-2.5 outline-none focus-visible:ring-4 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-within:ring-4 focus-within:ring-primary/60 focus-within:ring-offset-2"
		>
			<video
				{...restProps}
				ref={videoRef}
				autoPlay
				playsInline
				poster={winPaper}
				className={[
					"m-auto h-full w-full cursor-crosshair object-contain border-0 p-0",
					className,
				]
					.filter(Boolean)
					.join(" ")}
			></video>
			{/* 隐藏输入框：承接 input + compositionend，给 IME 用 */}
			<input
				ref={imeRef}
				type="text"
				aria-label="Remote keyboard input"
				tabIndex={-1}
				autoComplete="off"
				spellCheck={false}
				className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
				style={{ left: -9999, top: -9999 }}
			/>
		</div>
	);
};
